import React, { useEffect, useRef, useState } from 'react';
import { SkeletonGroup, SkeletonThumbnail } from '@shared/components/Skeleton';
import { LazyScreenProps } from './LazyScreen.types';
import { addError } from '@shared/utils/logger';

export const LazyScreen: React.FC<LazyScreenProps> = props => {
  const { height, width, children, src, aspectRatio, showSkeleton, skeletonGroupOverrides } = props;
  const initializedRef = useRef(false);
  const [loaded, setLoaded] = useState(src === '');

  const handleOnLoad = () => {
    setLoaded(true);
  };

  useEffect(() => {
    if (!src) {
      // if no src, set loaded to true to render children
      handleOnLoad();
      return;
    }

    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    const img = new Image();

    img.onload = handleOnLoad;
    // if the image fails to load, set loaded to true to render children
    // otherwise, the skeleton will be shown indefinitely
    img.onerror = () => {
      addError('LazyScreen: Image failed to load', { context: 'LazyScreen', src });
      handleOnLoad();
    };

    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [src]);

  return (
    <SkeletonGroup
      isReady={loaded && !showSkeleton}
      overrides={skeletonGroupOverrides}
      placeholder={
        <SkeletonThumbnail
          height={height}
          width={width}
          aspectRatio={aspectRatio}
        />
      }
    >
      {children(loaded)}
    </SkeletonGroup>
  );
};

LazyScreen.displayName = 'LazyScreen';
